"use client";

// Виджет «Заявки по отделам» (вкладка «Задачи»): по каждому отделу — сколько
// заявок на доработку (dev_requests) выполнено / всего. Горизонтальные бары
// в стиле «Этапы запуска» из дашборда R&D. Клик по строке отдела → поповер
// со списком НЕвыполненных заявок (new + in_progress).

import { useEffect, useMemo, useState } from "react";
import { Inbox } from "lucide-react";
import { IconCheck, IconClose} from "@/components/ui/icons";

import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Department, DevRequest, DevRequestPriority } from "@/lib/types";
import { Badge } from "@/components/ui/badge";

const PRIO_COLOR: Record<DevRequestPriority, string> = { low: "#22c55e", normal: "#f59e0b", high: "#ef4444" };

export function ItprDeptRequestsWidget({ onOpenReq, reloadKey = 0, компактно = false }: { onOpenReq?: (req: DevRequest, el: HTMLElement) => void; reloadKey?: number; компактно?: boolean } = {}) {
  // Компактный режим — для кабинета: пять блоков в ряд, на карточку ~300 px.
  const [reqs, setReqs] = useState<DevRequest[]>([]);
  const [depts, setDepts] = useState<Department[]>([]);
  const [openDept, setOpenDept] = useState<string | null>(null);  // какой отдел раскрыт
  useEffect(() => { api.getDepartments().then(setDepts).catch(() => {}); }, []);
  // Заявки перезагружаются по сигналу reloadKey (после конвертации/отклонения).
  useEffect(() => { api.listDevRequests().then(setReqs).catch(() => {}); }, [reloadKey]);

  // По каждому отделу: всего (кроме отклонённых), выполнено (done) и список открытых.
  const rows = useMemo(() => {
    const list = depts.filter((d) => d.key !== "system");
    return list.map((d) => {
      const mine = reqs.filter((r) => r.dept_key === d.key && r.status !== "rejected");
      const open = mine.filter((r) => r.status !== "done");
      const total = mine.length;
      const done = total - open.length;
      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
      const color = total === 0 ? "#64748b" : done >= total ? "#10b981" : done > 0 ? "#f59e0b" : "#94a3b8";
      return { key: d.key, title: d.title, total, done, pct, color, open };
    });
  }, [reqs, depts]);

  const totalAll = rows.reduce((s, r) => s + r.total, 0);
  const doneAll = rows.reduce((s, r) => s + r.done, 0);
  const overallPct = totalAll > 0 ? Math.round((doneAll / totalAll) * 100) : 0;

  return (
    <div className="glass-panel flex min-h-0 flex-1 flex-col rounded-[var(--radius-lg)] border border-[var(--border)] p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid shrink-0 place-items-center rounded-[var(--radius-md)]" style={{ width: 30, height: 30, background: "color-mix(in srgb, #06b6d4 16%, var(--card))", color: "#06b6d4" }}>
          <Inbox size={16} />
        </span>
        <span className="truncate text-[12px] font-bold text-[var(--text)]" >Заявки на доработку</span>
        <Badge color="#06b6d4" className="ml-auto tabular-nums">{overallPct}%</Badge>
      </div>
      {!компактно && (
        <div className="mb-3 -mt-1 text-[11px] text-[var(--textMuted)]">Заявок на доработку выполнено / всего · нажмите отдел, чтобы увидеть открытые.</div>
      )}

      <div className={`flex flex-col ${компактно ? "gap-1" : "gap-2"}`}>
        {rows.map((r) => {
          const row = (
            <div className={`flex items-center ${компактно ? "gap-1.5" : "gap-2.5"}`}>
              <div className={`shrink-0 truncate text-right font-semibold ${компактно ? "w-[74px] text-[11px]" : "w-24 text-[12px]"}`} data-tooltip={r.title}>{r.title}</div>
              <div className={`relative min-w-0 flex-1 overflow-hidden rounded-[var(--radius-md)] bg-[color-mix(in_srgb,var(--border)_40%,transparent)] ${компактно ? "h-3.5" : "h-5"}`}>
                <div className="h-full rounded-[var(--radius-md)] transition-all"
                  style={{ width: `${r.pct}%`, background: `linear-gradient(90deg, color-mix(in srgb, ${r.color} 65%, transparent), ${r.color})` }} />
              </div>
              <div className={`shrink-0 text-right font-semibold tabular-nums ${компактно ? "w-10 text-[11px]" : "w-12 text-[12px]"}`}>
                <span style={{ color: r.color }}>{r.done}</span><span className="text-[var(--textMuted)]">/{r.total}</span>
              </div>
            </div>
          );
          if (r.total === 0) {
            return <div key={r.key} className="rounded-[var(--radius-md)] px-1 py-0.5 opacity-60">{row}</div>;
          }
          return (
            <Popover key={r.key} open={openDept === r.key} onOpenChange={(o) => setOpenDept(o ? r.key : null)}>
              <PopoverTrigger asChild>
                <button type="button" style={{ boxShadow: "none" }} className="rounded-[var(--radius-md)] px-1 py-0.5 text-left transition hover:bg-[var(--hover)]"
                  data-tooltip="Показать открытые заявки">{row}</button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={6} className="w-[300px] p-0"
                onOpenAutoFocus={(e) => e.preventDefault()}
                onInteractOutside={(e) => {
                  // Клик/фокус внутри открытой карточки заявки — список НЕ закрываем;
                  // клик мимо карточки и мимо списка — закрываем (поведение radix по умолчанию).
                  const t = (e.detail as { originalEvent?: Event })?.originalEvent?.target as HTMLElement | null;
                  if (t && t.closest("[data-req-panel]")) e.preventDefault();
                }}>
                <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
                  <span className="text-[12px] font-bold" >{r.title}</span>
                  <span className="flex items-center gap-1.5">
                    <span className="text-[11px] text-[var(--textMuted)]">открытых: {r.open.length}</span>
                    <button type="button" onClick={() => setOpenDept(null)} className="rounded-[var(--radius-md)] p-0.5 text-[var(--textMuted)] hover:text-[var(--text)]" data-tooltip="Закрыть"><IconClose size={13} /></button>
                  </span>
                </div>
                <div className="max-h-72 overflow-y-auto scrollbar-autohide p-2">
                  {r.open.length === 0 ? (
                    <div className="flex items-center justify-center gap-1.5 py-5 text-center text-[12px] text-[var(--textMuted)]">Все заявки выполнены <IconCheck size={14} /></div>
                  ) : (
                    <ul className="flex flex-col gap-2">
                      {r.open.map((q) => (
                        <li key={q.id}>
                          <button type="button" data-tooltip="Открыть заявку"
                            onClick={(e) => onOpenReq?.(q, e.currentTarget)}
                            className="glass-flat outline-cell flex w-full flex-col gap-1 rounded-[var(--radius-lg)] px-2.5 py-2 text-left transition hover:-translate-y-px"
                            style={{ ["--gc"]: PRIO_COLOR[q.priority] ?? "#64748b", background: "var(--card)" } as React.CSSProperties}>
                            <span className="truncate text-[11px] font-semibold leading-tight text-[var(--text)]" >{q.title}</span>
                            <span className="truncate text-[11px] leading-tight text-[var(--textMuted)]">{q.tab_label || "Заявка на доработку"}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          );
        })}
        {rows.length === 0 && <div className="py-6 text-center text-[12px] text-[var(--textMuted)]">Нет отделов</div>}
      </div>
    </div>
  );
}
